// dso-module.js — standalone, role-gated DSO Framework module.
//
// Joe's DSO theory must not pollute the published-physics domains. Rather than
// hide it in place, this RELOCATES the DSO content out of the Cosmos and Theory
// modules into a dedicated DSO module, gated to the `admin` / `dso` role. After
// relocation the published domains contain only credible, peer-reviewable work:
//   - Cosmos keeps CMB, Black-Hole Jets, P(k), Press-Schechter, Acoustic
//     Horizon, Weak Lensing, Validation (real WASM engines).
//   - Theory keeps math / physics / geology and the non-DSO frameworks.
//
// The panels are moved by DOM relocation (their element IDs are preserved, so
// the form handlers and WASM render functions bound at init keep working). A
// dedicated tab handler drives the DSO view via inline display, robust against
// the source modules' scoped CSS.

import { getLicense } from './auth.js';

// (source panel id, label, icon, source module). NOTE: Q-Desic (cp-qdesic-cosmo)
// is deliberately NOT here - it is a published external theory (Koch, Riahinia &
// Rincon 2025, quantum-corrected geodesics, arXiv:2510.00117), NOT Joe's DSO, so
// it is migrated into Theory -> Frameworks instead (see migrateGenuineToTheory).
const DSO_ITEMS = [
  { id: 'cp-rotation',       label: 'Galaxy Rotation', icon: 'fa-sync-alt', from: 'cosmic' },
  { id: 'cp-constants',      label: 'Golden Constants', icon: 'fa-star',     from: 'cosmic' },
  { id: 'cp-ratios',         label: 'Cosmic Ratios',    icon: 'fa-divide',   from: 'cosmic' },
  { id: 'ts-fw-seeth',       label: 'Seeth Explorer',   icon: 'fa-infinity', from: 'theory' },
  { id: 'ts-fw-simulations', label: 'DSO Simulations',  icon: 'fa-flask',    from: 'theory' },
];

// Genuine non-DSO theories that were grouped with DSO get relocated into the
// published Theory module (Frameworks domain) with no DSO ties. Currently:
// Q-Desic (quantum-corrected geodesics, Koch/Riahinia/Rincon 2025).
export function migrateGenuineToTheory() {
  const panel = document.getElementById('cp-qdesic-cosmo');
  const frameworks = document.getElementById('td-frameworks');
  if (!panel || !frameworks) return;
  const anySubtab = frameworks.querySelector('.theory-subtab');
  const subtabBar = anySubtab ? anySubtab.parentElement : null;
  if (!subtabBar || subtabBar.querySelector('[data-ts="qdesic"]')) return;

  // Drop the orphaned Cosmos tab button and re-home the panel as a Theory
  // Frameworks subpanel (drop the cosmic-panel class so only the theory-subpanel
  // CSS governs its visibility, matching the native frameworks subtabs).
  document.querySelector('.ctab[data-ct="qdesic-cosmo"]')?.remove();
  panel.classList.remove('cosmic-panel', 'active');
  panel.classList.add('theory-subpanel');
  frameworks.appendChild(panel);

  const btn = document.createElement('button');
  btn.className = 'theory-subtab';
  btn.dataset.ts = 'qdesic';
  btn.title = 'Q-Desic';
  btn.innerHTML = '<i class="fas fa-route"></i> Q-Desic';
  btn.addEventListener('click', () => {
    subtabBar.querySelectorAll('.theory-subtab').forEach(b => b.classList.remove('active'));
    frameworks.querySelectorAll('.theory-subpanel').forEach(p => p.classList.remove('active'));
    btn.classList.add('active');
    panel.classList.add('active');
  });
  subtabBar.appendChild(btn);
}

let built = false;

function activate(panelId) {
  const tabbar = document.getElementById('dso-tabbar');
  const panels = document.getElementById('dso-panels');
  tabbar?.querySelectorAll('.ctab').forEach(b => b.classList.toggle('active', b.dataset.dt === panelId));
  panels?.querySelectorAll(':scope > div').forEach(p => {
    const on = p.id === panelId;
    p.style.display = on ? 'block' : 'none';
    p.classList.toggle('active', on);
  });
}

export function buildDsoModule() {
  if (built) return;
  const tabbar = document.getElementById('dso-tabbar');
  const panels = document.getElementById('dso-panels');
  if (!tabbar || !panels) return;

  let firstId = null;
  DSO_ITEMS.forEach(item => {
    const panel = document.getElementById(item.id);
    if (!panel) return;
    // Remove the now-orphaned tab button in the source module so Cosmos / Theory
    // tab bars no longer show DSO.
    if (item.from === 'cosmic') {
      document.querySelector(`.ctab[data-ct="${item.id.replace(/^cp-/, '')}"]`)?.remove();
    } else {
      document.querySelector(`.theory-subtab[data-ts="${item.id.replace(/^ts-/, '')}"]`)?.remove();
    }
    panel.classList.remove('active');
    panel.style.display = 'none';
    panels.appendChild(panel);
    const btn = document.createElement('button');
    btn.className = 'ctab';
    btn.dataset.dt = item.id;
    btn.title = item.label;
    btn.innerHTML = `<i class="fas ${item.icon}"></i> ${item.label}`;
    btn.addEventListener('click', () => activate(item.id));
    tabbar.appendChild(btn);
    if (!firstId) firstId = item.id;
  });

  if (firstId) activate(firstId);

  // Cosmos default tab was "rotation" (now relocated) — give it a genuine
  // published-cosmology default so the module isn't left blank.
  if (!document.querySelector('#cos-content .ctab.active')) {
    document.querySelector('#cos-content .ctab[data-ct="cmb"]')?.classList.add('active');
    document.getElementById('cp-cmb')?.classList.add('active');
  }

  built = true;
}

export function applyDsoGating() {
  const license = getLicense();
  const tier = license?.tier || 'trial';
  const isDso = tier === 'admin' || (license?.modules || []).includes('dso');
  const nav = document.querySelector('[data-view="dso"]');
  const view = document.getElementById('view-dso');
  if (nav) nav.style.display = isDso ? '' : 'none';
  if (!isDso && view && view.classList.contains('active')) {
    // A non-DSO user should never be parked on the DSO view; send them home.
    view.classList.remove('active');
    document.querySelector('.nav-item[data-view="materials"]')?.click();
  }
}
